import { useCallback } from "react";
import { useRouter } from "next/router";
import { useAtomValue } from "jotai";
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";

import { ortexColorState, textColorState, themeState, userState } from "@/lib/store";
import chatInputStyles from "../chat-input/ChatInput.module.scss";
import useAppSubscriberUpgrade from "src/hooks/useAppSubscriberUpgrade";
import { UPGRADE_LOCATIONS, trackUpgradeClick } from "@/lib/tracking";

interface ObfuscatedFiltersDialogProps {
    open: boolean;
    onClose: () => void;
}

const ObfuscatedFiltersDialog = ({ open, onClose }: ObfuscatedFiltersDialogProps) => {
    const ortexColor = useAtomValue(ortexColorState);
    const textColor = useAtomValue(textColorState);
    const theme = useAtomValue(themeState);
    const { subscription_plan } = useAtomValue(userState);
    const router = useRouter();

    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();

    const handleUpgradeModalClick = useCallback(() => {
        trackUpgradeClick(UPGRADE_LOCATIONS.OBFUSCATED_FILTERS_DIALOG, { plan: subscription_plan });
        onClose();
        if (subscription_plan === "ORTEX Visitor") {
            router.push("/login?register=true");
        } else {
            handleAppSubscriberUpgrade();
        }
    }, [router, subscription_plan, onClose, handleAppSubscriberUpgrade]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            slotProps={{
                backdrop: { className: chatInputStyles.loginDialogBackdrop },

                paper: {
                    className: `${chatInputStyles.loginDialogPaper}${theme === "dark" ? ` ${chatInputStyles.dark}` : ""}`,
                },
            }}
        >
            <DialogContent className={chatInputStyles.loginDialogContent} sx={{ color: textColor }}>
                <Box className={chatInputStyles.loginDialogIconWrap} sx={{ "& .MuiSvgIcon-root": { color: ortexColor } }}>
                    <TuneIcon />
                </Box>
                <Typography variant="h6" className={chatInputStyles.loginDialogTitle}>
                    {subscription_plan === "ORTEX Visitor" ? "Unlock filters and more with a free account" : "Upgrade your subscription to access filters and more"}
                </Typography>
                <Typography variant="body2" className={chatInputStyles.loginDialogBody}>
                    {`${subscription_plan === "ORTEX Visitor" ? "Sign up for a free account" : "Upgrade now"} to unlock powerful filters, personalized insights, and the ability to save your settings for a tailored experience.`}
                </Typography>
            </DialogContent>
            <DialogActions className={chatInputStyles.loginDialogActions}>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleUpgradeModalClick}
                    className={chatInputStyles.loginDialogPrimaryBtn}
                    sx={{ bgcolor: `${ortexColor} !important`, color: "#fff", "&:hover": { bgcolor: `${ortexColor} !important`, filter: "brightness(1.1)" } }}
                >
                    {subscription_plan === "ORTEX Visitor" ? "Create free account" : "Upgrade now"}
                </Button>
                <Button variant="text" onClick={onClose} className={chatInputStyles.loginDialogSecondaryBtn} sx={{ color: textColor }}>
                    Not now
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ObfuscatedFiltersDialog;
